import { Box } from '@superdispatch/ui-lab';
import { useMemo } from 'react';
import { useAppFormik } from 'shared/form/AppFormik';
import {
  FormikDrawer,
  FormikDrawerActions,
  FormikDrawerContent,
} from 'shared/form/FormikDrawer';
import { castAPIData } from 'shared/api/APISchema';
import {
  LoadSubhaulerPaymentDTO,
  formatLoadStatus,
  loadSubhaulerPaymentSchema,
} from '../data/LoadDTO';
import { trackLoadsEvent } from '../data/LoadsAnalytics';
import { useLoadDetails, useLoadsAPI, useLoadsCache } from '../data/LoadsAPI';
import { useLoadStageFromBackURL } from '../view/ViewLoadHeaderActions';
import { useSubhaulLoad } from '../view/useSubhaulLoad';
import { Column, Columns, Stack } from '@superdispatch/ui';
import { FormikCurrencyField, FormikTextField } from '@superdispatch/forms';
import { getIn } from 'formik';
import { isEqual } from 'es-toolkit';
import { PaymentMethod, formatPaymentTerm } from '@superdispatch/sdk';
import {
  CARRIER_PAYMENT_METHODS,
  formatCarrierPaymentMethod,
} from 'shared/helpers/CarrierPaymentTypes';
import { MenuItem, Typography } from '@material-ui/core';
import { listCarrierPaymentTerms } from 'shared/helpers/CarrierPaymentTypes';

interface EditSubhaulerPaymentAgreementDrawerContentProps {
  loadGUID: string;
  onClose: () => void;
}

function EditSubhaulerPaymentAgreementDrawerContent({
  onClose,
  loadGUID,
}: EditSubhaulerPaymentAgreementDrawerContentProps) {
  const { isSubhauled } = useSubhaulLoad();
  const { data } = useLoadDetails(loadGUID, { isSubhauled });
  const { editLoadSubhaulerPayment } = useLoadsAPI();
  const { invalidateLoads } = useLoadsCache();
  const loadStage = useLoadStageFromBackURL();

  const initialValues = {
    customer: {
      price: data?.customer_payment?.price || null,
      payment_method: data?.customer_payment?.payment_method,
      payment_terms: data?.customer_payment?.payment_terms,
      payment_notes: data?.customer_payment?.payment_notes,
    },
    subhauler: {
      price: data?.subhauler_payment?.price,
      payment_method: data?.subhauler_payment?.payment_method,
      payment_terms: data?.subhauler_payment?.payment_terms,
    },
  };

  const formik = useAppFormik({
    initialValues,
    validationSchema: loadSubhaulerPaymentSchema,
    onSubmit(values) {
      const castedInitial = castAPIData(
        loadSubhaulerPaymentSchema,
        initialValues,
      );
      const changedValues = Object.fromEntries(
        Object.entries(values).filter(
          ([group, fields]) =>
            !isEqual(
              fields,
              castedInitial[group as keyof typeof castedInitial],
            ),
        ),
      ) as LoadSubhaulerPaymentDTO;

      if (changedValues.customer != null) {
        const initialPrice = castedInitial.customer?.price;
        changedValues.customer = {
          ...changedValues.customer,
          price:
            changedValues.customer.price !== initialPrice
              ? (changedValues.customer.price ?? 0)
              : changedValues.customer.price,
        };
      }

      return editLoadSubhaulerPayment(loadGUID, changedValues);
    },
    onSubmitSuccess() {
      onClose();
      invalidateLoads();

      trackLoadsEvent({
        name: 'Carrier Edited Load',
        utm_medium: loadStage,
        utm_content: 'View Page',
        load_status: formatLoadStatus(data?.status),
        is_created_by_broker: !!data?.is_created_by_broker,
        payment_information: 'Edited',
      });
    },
    getSuccessMessage() {
      return 'Payment information successfully updated';
    },
    getErrorMessage({ message }) {
      return message || 'Something went wrong, contact support.';
    },
  });

  const { values, setFieldValue } = formik;

  const paymentMethod = getIn(
    values,
    'customer.payment_method',
  ) as PaymentMethod;

  const subhaulerPaymentMethod = getIn(
    values,
    'subhauler.payment_method',
  ) as PaymentMethod;

  const paymentMethods = useMemo(() => {
    // Shipper can only use superpay
    if (paymentMethod !== 'superpay') {
      return CARRIER_PAYMENT_METHODS.filter((type) => type !== 'superpay');
    }

    return CARRIER_PAYMENT_METHODS;
  }, [paymentMethod]);

  const subhaulerPaymentMethods = useMemo(() => {
    // Shipper can only use superpay
    if (subhaulerPaymentMethod !== 'superpay') {
      return CARRIER_PAYMENT_METHODS.filter((type) => type !== 'superpay');
    }

    return CARRIER_PAYMENT_METHODS;
  }, [subhaulerPaymentMethod]);

  return (
    <FormikDrawerContent
      title="Payment Agreement"
      formik={formik}
      onClose={onClose}
      actions={<FormikDrawerActions name="loads" permission="can_manage" />}
    >
      <Box maxWidth="368px">
        <Stack space="small">
          <FormikCurrencyField
            fullWidth={true}
            label="Original Price"
            name="customer.price"
            helperText="Paid by customer to you"
            inputProps={{ disableValueParsing: true }}
          />

          <Columns space="small" collapseBelow="desktop">
            <Column width="1/2">
              <FormikTextField
                select={true}
                fullWidth={true}
                label="Payment Method"
                name="customer.payment_method"
                onChange={() => {
                  void setFieldValue('customer.payment_terms', null, false);
                }}
              >
                {paymentMethods.map((key) => (
                  <MenuItem key={key} value={key}>
                    {formatCarrierPaymentMethod(key)}
                  </MenuItem>
                ))}
              </FormikTextField>
            </Column>

            <Column width="1/2">
              <FormikTextField
                select={true}
                fullWidth={true}
                label="Terms"
                disabled={!paymentMethod}
                name="customer.payment_terms"
              >
                {listCarrierPaymentTerms(paymentMethod).map((key) => {
                  return (
                    <MenuItem key={key} value={key}>
                      {formatPaymentTerm(key)}
                    </MenuItem>
                  );
                })}
              </FormikTextField>
            </Column>
          </Columns>

          <FormikTextField
            label="Notes"
            multiline={true}
            fullWidth={true}
            name="customer.payment_notes"
          />

          <Typography variant="h4">Sub-hauling Details</Typography>

          <FormikCurrencyField
            fullWidth={true}
            name="subhauler.price"
            label="Sub-hauling Price"
            helperText="Paid by you to sub-hauler"
          />

          <Columns space="small" collapseBelow="desktop">
            <Column width="1/2">
              <FormikTextField
                select={true}
                fullWidth={true}
                label="Payment Method"
                name="subhauler.payment_method"
                onChange={() => {
                  void setFieldValue('subhauler.payment_terms', null, false);
                }}
              >
                {subhaulerPaymentMethods.map((key) => (
                  <MenuItem key={key} value={key}>
                    {formatCarrierPaymentMethod(key)}
                  </MenuItem>
                ))}
              </FormikTextField>
            </Column>

            <Column width="1/2">
              <FormikTextField
                select={true}
                fullWidth={true}
                label="Terms"
                disabled={!subhaulerPaymentMethod}
                name="subhauler.payment_terms"
              >
                {listCarrierPaymentTerms(subhaulerPaymentMethod).map((key) => {
                  return (
                    <MenuItem key={key} value={key}>
                      {formatPaymentTerm(key)}
                    </MenuItem>
                  );
                })}
              </FormikTextField>
            </Column>
          </Columns>
        </Stack>
      </Box>
    </FormikDrawerContent>
  );
}

interface EditPaymentAgreementDrawerProps extends EditSubhaulerPaymentAgreementDrawerContentProps {
  open: boolean;
}

export function EditSubhaulerPaymentAgreementDrawer({
  open,
  onClose,
  loadGUID,
}: EditPaymentAgreementDrawerProps) {
  return (
    <FormikDrawer open={open} onClose={onClose}>
      <EditSubhaulerPaymentAgreementDrawerContent
        onClose={onClose}
        loadGUID={loadGUID}
      />
    </FormikDrawer>
  );
}
